import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { History, ArrowRight, MessageSquare, Plus } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ACTION_STYLES = {
  stage_change: { color: 'bg-info/20 text-info', icon: ArrowRight },
  note: { color: 'bg-muted text-foreground', icon: MessageSquare },
  email_sent: { color: 'bg-success/20 text-success', icon: MessageSquare },
  interview_scheduled: { color: 'bg-warning/20 text-warning', icon: History },
  rating_update: { color: 'bg-purple-100 text-purple-700', icon: History },
};

export function ApplicantActivityDialog({ applicantId, applicantName, open, onOpenChange }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (open && applicantId) fetchActivities();
  }, [open, applicantId]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/applicants/${applicantId}/activity`);
      setActivities(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/applicants/${applicantId}/activity`, { action: 'note', details: newNote });
      setNewNote('');
      fetchActivities();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="activity-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History size={16} />Activity: {applicantName}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[50dvh] overflow-y-auto">
          {/* Add note */}
          <div className="flex gap-2">
            <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="flex-1" data-testid="activity-note-input" onKeyDown={e => e.key === 'Enter' && handleAddNote()} />
            <Button size="sm" onClick={handleAddNote} data-testid="add-note-btn"><Plus size={14} /></Button>
          </div>

          {loading ? <div className="text-center py-4 text-xs text-muted-foreground">Loading...</div> :
            activities.length === 0 ? <div className="text-center py-4 text-xs text-muted-foreground">No activity yet</div> :
            <div className="space-y-2">
              {activities.map(a => {
                const style = ACTION_STYLES[a.action] || ACTION_STYLES.note;
                const Icon = style.icon;
                return (
                  <div key={a.id} className="flex gap-2 p-2 rounded-lg border bg-muted/50" data-testid={`activity-${a.id}`}>
                    <div className={`mt-0.5 p-1 rounded ${style.color}`}><Icon size={10} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge className={`text-[9px] border-0 ${style.color}`}>{a.action.replace('_', ' ')}</Badge>
                        <span className="text-[10px] text-muted-foreground">{a.user_name}</span>
                      </div>
                      <div className="text-xs mt-0.5">{a.details}</div>
                      {a.from_stage && a.to_stage && <div className="text-[10px] text-muted-foreground mt-0.5">{a.from_stage} &rarr; {a.to_stage}</div>}
                      <div className="text-[9px] text-muted-foreground mt-0.5">{new Date(a.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          }
        </div>
      </DialogContent>
    </Dialog>
  );
}
